import { calculateRawScores, DIMS_12, THEORETICAL_BOUNDS, routePool } from './scoringEngine';
import { DimensionKey } from '../types';
import { Dimension12D } from '../data/characterRegistry';
import { FORMAL_QUESTIONS } from '../data/mockQuizData';

export type VerdictLevel = 'normal' | 'notice' | 'hazard' | 'critical';

export interface DynamicVerdict {
  text: string;
  level: VerdictLevel;
  stateTag: string;
  badgeText: string;
  stageCode: string;
}

// 1. 语料库定义（按 10 大状态分类与 4 个答题阶段分层构建，每条严格控制在 12~15 字以内，彻底避免移动端溢出截断）
const VERDICT_POOLS: Record<string, Record<number, string[]>> = {
  // 0. 初始建档（Q01 专属）
  CALIBRATING: {
    1: [
      '【基线：生理与生活参数采集中】',
      '【接入：受试者档案已建立】',
      '【自检：伴侣拟合通道已就绪】',
      '【校准：正在排除外部干扰噪音】',
      '【提示：请依真实直觉如实作答】',
    ],
    2: [],
    3: [],
    4: [],
  },

  // 1. 现实承载警戒 (REALITY_CRITICAL)
  REALITY_CRITICAL: {
    1: [
      '【监测：时空冗余逼近阈值】',
      '【评估：维生账单存透支风险】',
      '【负荷：当前带宽难支持高耗能】',
      '【预警：照料时间配额已至极限】',
      '【注记：居住空间容积率告急】',
    ],
    2: [
      '【警告：自我维持系统过载运转】',
      '【标记：生活防线出现结构疲劳】',
      '【预警：抗突发风险能力处低位】',
      '【警报：电量不足以对生命托底】',
      '【承载：现实生活摩擦系数偏高】',
    ],
    3: [
      '【红警：常规容纳率跌破警戒】',
      '【熔断：物理饲养条件实质瓦解】',
      '【批注：建议放弃传统宠物幻想】',
      '【通报：防线失守，调替代物】',
      '【预警：常规拟合通道关闭】',
    ],
    4: [
      '【熔断：碳基伴侣通道永久关闭】',
      '【通报：强养宠物恐双向受损】',
      '【重定向：接入免维护收容体】',
    ],
  },

  // 2. 高阶自律稳态 (HIGH_STABILITY)
  HIGH_STABILITY: {
    1: [
      '【稳态：生活秩序高度收敛】',
      '【评估：具备成熟长期托底能力】',
      '【秩序：精力充沛未见损耗】',
      '【自洽：时间物质确定性高】',
      '【基准：生活锚点稳固资质优】',
    ],
    2: [
      '【记录：压力下维持理性防线】',
      '【带宽：情绪反馈稳耐受良好】',
      '【维持：对繁琐日常极高包容】',
      '【标定：展现成熟自律主体特征】',
      '【自理：压力下韧性未见衰退】',
    ],
    3: [
      '【惊异：现实自律坚固抵御诱导】',
      '【锁定：圈定高合规碳基伴侣库】',
      '【倾向：罕见样本，匹配锚点】',
      '【防御：系统失谐稳态如钟】',
      '【合规：具备十年生命契约心智】',
    ],
    4: [
      '【特批：成功突围全套压力测试】',
      '【终审：即将生成模范伴侣报告】',
      '【盖印：认证为模范饲养员候选】',
    ],
  },

  // 3. 意志休眠与彻底摆烂 (SLOTH_COLLAPSE)
  SLOTH_COLLAPSE: {
    1: [
      '【监测：功耗调低，被动待机】',
      '【观察：重复家务抗拒抬升】',
      '【能耗：能不动绝不挪窝】',
      '【偏好：物理避难，劳务断舍】',
    ],
    2: [
      '【记录：极速放弃局面掌控】',
      '【警报：受试者与床垫融合】',
      '【警告：维持现状意愿衰退】',
      '【节能：启动不抵抗躺平模式】',
      '【停机：主动经营关系将装死】',
    ],
    3: [
      '【通报：脑电波趋平放弃抵抗】',
      '【批注：出门遛弯生物将致命】',
      '【解构：生存逻辑蜕变为躺平】',
      '【告警：受试者瘫软在沙发缝】',
      '【退化：极速向无机形态演化】',
    ],
    4: [
      '【死线：动能归零配零耗体】',
      '【休眠：指派原地化水特长室友】',
      '【摆烂：生活已过成安详盆栽】',
    ],
  },

  // 4. 局势脱轨与失控狂欢 (CHAOS_SURGE)
  CHAOS_SURGE: {
    1: [
      '【节律：生活作息呈现随机漂移】',
      '【秩序：突发应变模型扩散中】',
      '【参数：生活规律度剧烈震荡】',
    ],
    2: [
      '【失控：正在脱轨，动能过载】',
      '【记录：检测到冲动破坏倾向】',
      '【扰动：崩塌耐受高伴有兴奋】',
      '【测定：对突发混乱反常适应】',
    ],
    3: [
      '【警报：家庭秩序被彻底踩碎】',
      '【失焦：受试者甚至还想加速】',
      '【危险：常规秩序已无法约束】',
      '【狂欢：把失控局面当游乐场】',
      '【畸变：崩坏速率超出系统上限】',
    ],
    4: [
      '【红警：受试者与失控力合体】',
      '【接管：准备移交莽夫级实体】',
      '【摊手：既然要拆不如拆更响】',
    ],
  },

  // 5. 非标解构与抽象同化 (ABSTRACT_DRIFT)
  ABSTRACT_DRIFT: {
    1: [
      '【认知：常规逻辑轻度疲劳】',
      '【语义：幽默雷达向非主流漂移】',
      '【潜流：表象下检测到解构冲动】',
    ],
    2: [
      '【观察：困境本能采用荒谬解】',
      '【偏轨：荒诞生活切片共鸣强】',
      '【跃迁：因果律在脑内逐渐失效】',
      '【异变：生活事故直接视作段子】',
    ],
    3: [
      '【记录：荒谬事件惊人同化】',
      '【畸变：因果失效，精神超前】',
      '【提示：常规生物拟合概率暴跌】',
      '【崩塌：常规物种难满足精神】',
    ],
    4: [
      '【同化：检测到高维抽象物共振】',
      '【越权：常规清洗魔性体接管】',
      '【批注：大脑免疫一切严肃说教】',
      '【力场：生成无厘头绝对防御】',
    ],
  },

  // 6. 规训权威排异与反骨 (REBELLION_REJECT)
  REBELLION_REJECT: {
    1: [
      '【权限：对标准化选项隐蔽排斥】',
      '【抗性：对说教指令天然钝感】',
    ],
    2: [
      '【抗性：对规训指令生理排斥】',
      '【反弹：越被规范，越反其道】',
      '【服从：骨子里刻着教条厌恶】',
    ],
    3: [
      '【记录：强烈敌意挑衅系统】',
      '【反叛：带说教指令遭当场反弹】',
      '【批注：反骨硬度超哺乳极限】',
      '【排异：无视标准反抗力外溢】',
    ],
    4: [
      '【冲突：明确拒绝被系统定义】',
      '【破局：顺从归零展开反击】',
      '【终审：指派顺从生物恐内战】',
      '【敌意：冷笑注视系统手段】',
    ],
  },

  // 7. 私域边界锁定与独立 (SOLO_DEFENSE)
  SOLO_DEFENSE: {
    1: [
      '【测定：偏好低干扰防线完整】',
      '【空间：私域不容侵犯能耗极简】',
    ],
    2: [
      '【独立：无需认同，自成闭环】',
      '【电量：独处静默时快速回血】',
      '【脱敏：亲密引警惕体面共存】',
    ],
    3: [
      '【通报：严禁高频活物强行破门】',
      '【契约：平行空间安全车距】',
      '【封死：向外索求情感端焊死】',
    ],
    4: [
      '【自洽：单体核心，拒绝打扰】',
      '【裁定：分配互不打扰终极室友】',
    ],
  },

  // 8. 情感依恋与活物吸附 (AFFECTION_HUNGER)
  AFFECTION_HUNGER: {
    1: [
      '【引力：吸附力强，排斥冷清】',
      '【渴望：无呼吸声将导致降能】',
    ],
    2: [
      '【带宽：需温热反馈维持活力】',
      '【伴侣：渴望无条件信任与偏爱】',
      '【锚点：渴望开门瞬间被扑倒】',
    ],
    3: [
      '【锁定：正在筛选全天候开心机】',
      '【预置：需要永远被视作核心】',
    ],
    4: [
      '【救赎：依恋接通配温热生命】',
      '【终审：将获扑向你的阳光宇宙】',
    ],
  },

  // 9. 神经负荷过载与高敏 (FRAGILE_ALERT)
  FRAGILE_ALERT: {
    1: [
      '【神经：细微环境噪音高度敏锐】',
      '【感知：生活细微摩擦高度警觉】',
    ],
    2: [
      '【警报：带宽临界严禁声光干扰】',
      '【应激：细微反常即触发防御】',
      '【避震：心力脆弱急需温和托底】',
    ],
    3: [
      '【保护：自动过滤一切喧闹体】',
      '【协议：调取低噪静默共生模型】',
    ],
    4: [
      '【隔离：排除喧哗配静默庇护】',
      '【闭环：脆弱非缺点配细腻灵魂】',
    ],
  },

  // 10. 终审高维接管 (ULTIMATE_CONTAINMENT)
  ULTIMATE_CONTAINMENT: {
    1: [],
    2: [],
    3: [],
    4: [
      '【收容：常态库全面放弃拟合】',
      '【警报：实体突破第三防线】',
      '【过载：精神超前导致系统发烫】',
      '【盖印：调取非认证收容协议】',
      '【通告：样本移交非常规中心】',
      '【熔断：对荒诞产生不可逆共鸣】',
      '【越权：常物无用不可名状接管】',
    ],
  },
};

export const STATE_BADGE_MAP: Record<string, string> = {
  'SYS-INIT': 'INIT',
  'HIGH_STABILITY': 'STABLE',
  'REALITY_CRITICAL': 'RISK',
  'SLOTH_COLLAPSE': 'SLOTH',
  'CHAOS_SURGE': 'CHAOS',
  'ABSTRACT_DRIFT': 'ABST',
  'REBELLION_REJECT': 'REBEL',
  'SOLO_DEFENSE': 'SOLO',
  'AFFECTION_HUNGER': 'BOND',
  'FRAGILE_ALERT': 'ALERT',
  'ULTIMATE_CONTAINMENT': 'OVERRIDE',
};

/**
 * ==================== 统一判定核心 ====================
 * 过程状态与最终结果同源：对"当前已作答"的数据外推正式 12D 归一化与 REALITY_RISK，
 * 再直接调用 scoringEngine.routePool 推导倾向（阈值单一事实来源，不复制第二套）。
 * 进度权重 w = 已答/35：早期估计向中性 50 收敛（不因少量答案给出强判定），
 * w=1 时外推精确等于正式 normalizeScores + routePool，最终状态与真实匹配在数学上必然一致。
 *   路由阈值（只在 routePool 中定义）：抽象越权 (A,X,R 组合) / G>=7 强制抽象 /
 *   G<=3 现实 / 4<=G<=6 按 abstractMean 临界判定（双池竞争带）
 */
interface Extrapolation {
  est: Record<string, number>; // 12D 外推归一化 0-100
  gEst: number; // 外推到全量的 REALITY_RISK raw 分
  abstractMean: number;
  progress: number; // 0-1
}

function extrapolateFromAnswers(answers: Record<number, string>): Extrapolation {
  const raw = calculateRawScores(answers);
  const answered = FORMAL_QUESTIONS.filter((q) => answers[q.id]);
  const n = answered.length;
  const w = n / FORMAL_QUESTIONS.length;

  const est: Record<string, number> = {};
  for (const d of DIMS_12) {
    // 已答题目在该维度上的理论最小/最大累计
    let pMin = 0;
    let pMax = 0;
    for (const q of answered) {
      let qMin = 0;
      let qMax = 0;
      for (const opt of q.options) {
        const v = opt.scores[d] || 0;
        if (v < qMin) qMin = v;
        if (v > qMax) qMax = v;
      }
      pMin += qMin;
      pMax += qMax;
    }
    const span = pMax - pMin;
    // 当前作答风格在该维度的相对位置（0-1），外推全量后按进度向中性收缩；
    // 与正式 normalizeScores 一致地取整，保证 w=1 时逐位等于正式归一化，
    // 消除浮点误差在路由阈值边界（如 abstractMean=50）上的翻转
    const ratio = span > 0 ? (raw[d] - pMin) / span : 0.5;
    est[d] = Math.round(50 * (1 - w) + Math.max(0, Math.min(1, ratio)) * 100 * w);
  }

  // G 为 raw 阈值体系：按已答理论满分占比线性外推到全量满分（取整对齐整数阈值体系）
  let gPartMax = 0;
  for (const q of answered) {
    let qMax = 0;
    for (const opt of q.options) {
      const v = opt.scores.G || 0;
      if (v > qMax) qMax = v;
    }
    gPartMax += qMax;
  }
  const gRatio = gPartMax > 0 ? (raw.G ?? 0) / gPartMax : 0;
  const gEst = Math.round(gRatio * (THEORETICAL_BOUNDS.G.max || 1));

  const abstractMean = (est.X + est.A + est.B + est.R) / 4;
  return { est, gEst, abstractMean, progress: w };
}

interface Tendency {
  pool: 'real' | 'abstract';
  isOverride: boolean;
  gCritical: boolean; // G>=7 现实承载力熔断驱动（区别于人格型抽象）
}

// 直接复用正式 routePool（唯一阈值来源）：routePool 只读取 rawScores.G 与 normalized 的 A/X/R/B
function classifyTendency(ex: Extrapolation): Tendency {
  const routing = routePool(
    { G: ex.gEst } as Record<DimensionKey, number>,
    ex.est as Record<Dimension12D, number>
  );
  return {
    pool: routing.pool,
    isOverride: routing.isAbstractOverride,
    gCritical: !routing.isAbstractOverride && routing.realityRisk >= 7,
  };
}

// 主导抽象人格维度对应的语料池（仅抽象倾向时使用；阈值 60 保证确有主导维度）
function dominantAbstractPool(est: Record<string, number>): string {
  const ranked = [
    ['ABSTRACT_DRIFT', est.A],
    ['CHAOS_SURGE', est.X],
    ['SLOTH_COLLAPSE', est.B],
    ['REBELLION_REJECT', est.R],
  ].sort((a, b) => (b[1] as number) - (a[1] as number));
  return (ranked[0][1] as number) >= 60 ? (ranked[0][0] as string) : 'ABSTRACT_DRIFT';
}

// 主导现实特征语料池（仅现实倾向时使用；高失控/高摆烂在现实池内只作特征观察，如哈士奇型样本）
function dominantRealityPool(est: Record<string, number>): string {
  const ranked = [
    ['AFFECTION_HUNGER', est.C],
    ['FRAGILE_ALERT', est.S],
    ['SOLO_DEFENSE', est.I],
    ['CHAOS_SURGE', est.X],
    ['SLOTH_COLLAPSE', est.B],
    ['HIGH_STABILITY', (est.M + est.E) / 2 + 10],
  ].sort((a, b) => (b[1] as number) - (a[1] as number));
  return (ranked[0][1] as number) >= 70 ? (ranked[0][0] as string) : 'HIGH_STABILITY';
}

/**
 * 动态根据当前截至做完的题目，实时计算受试者最突出的特征并生成注记。
 * 级别语义（与最终路由方向严格挂钩）：
 *   normal   观察状态：当前倾向现实宠物
 *   notice   过渡状态：两方向仍存竞争 / 早期抽象苗头 / 现实风险观察 / 突出现实特征
 *   hazard   异常状态：明显倾向抽象生物（进度至少 25%）
 *   critical 红色呼吸：高度明确的抽象判定（进度过半 + 强信号），不允许最终无解释地落在现实池
 */
export function evaluateDynamicVerdict(
  answers: Record<number, string>,
  currentIndex: number // 0-based index of the question currently displayed
): DynamicVerdict {
  // Q01 刚进入，尚无答题记录
  if (currentIndex === 0 || Object.keys(answers).length === 0) {
    const pool = VERDICT_POOLS.CALIBRATING[1];
    const text = pool[0];
    return {
      text,
      level: 'normal',
      stateTag: 'SYS-INIT',
      badgeText: 'INIT',
      stageCode: 'STAGE 1: 现实基准',
    };
  }

  // 计算当前题目的 stage (1: Q01-10, 2: Q11-20, 3: Q21-28, 4: Q29-35)
  const currentStage: 1 | 2 | 3 | 4 =
    currentIndex < 10 ? 1 : currentIndex < 20 ? 2 : currentIndex < 28 ? 3 : 4;

  const ex = extrapolateFromAnswers(answers);
  const tendency = classifyTendency(ex);

  let level: VerdictLevel;
  let stateTag: string;

  if (tendency.pool === 'abstract') {
    // 强信号：人格型抽象越权、现实熔断，或抽象均值显著过半
    const strong =
      tendency.isOverride || ex.gEst >= 7.5 || ex.abstractMean >= 62;
    if (ex.progress >= 0.5 && strong) {
      level = 'critical';
    } else if (ex.progress >= 0.25) {
      level = 'hazard';
    } else {
      level = 'notice'; // 早期抽象苗头只作过渡提示，不给强判定
    }

    if (tendency.isOverride) {
      stateTag =
        level === 'critical' ? 'ULTIMATE_CONTAINMENT' : dominantAbstractPool(ex.est);
    } else if (ex.abstractMean >= 62) {
      stateTag = dominantAbstractPool(ex.est);
    } else {
      // 现实承载力熔断驱动的抽象路由：用现实风险文案，不用"疯狂抽象"暗示
      stateTag = 'REALITY_CRITICAL';
    }
  } else {
    // 现实池方向
    if (ex.gEst >= 4) {
      // 现实养宠风险偏高是独立信号：停留在观察级，不使用抽象暗示色
      stateTag = 'REALITY_CRITICAL';
      level = 'notice';
    } else if (ex.est.A >= 75 && ex.abstractMean >= 58 && ex.progress >= 0.55) {
      // 抽象越权逼近带：人格型抽象信号逼近路由线（A≥75 且抽象均值≥58），
      // 提前进入 hazard 过渡，避免终局从 normal 直接跳变 critical
      stateTag = dominantAbstractPool(ex.est);
      level = 'hazard';
    } else {
      stateTag = dominantRealityPool(ex.est);
      const prominent =
        Math.max(ex.est.C, ex.est.S, ex.est.I, ex.est.X, ex.est.B) >= 78;
      level = prominent && ex.progress >= 0.4 ? 'notice' : 'normal';
    }
  }

  // 2. 从对应状态与 Stage 的语料池中抽取判词
  const statePools = VERDICT_POOLS[stateTag] || VERDICT_POOLS.HIGH_STABILITY;
  // 现实风险文案的语气分级：非红警级（倾向提示）只用 stage1-2 的观察/预警语料，
  // stage3-4 的"红警/熔断/调替代物"等裁决式收容暗示仅保留给 critical（此时路由已确定进抽象池）
  let poolStage: 1 | 2 | 3 | 4 = currentStage;
  if (stateTag === 'REALITY_CRITICAL' && level !== 'critical') {
    poolStage = currentStage >= 3 ? 2 : currentStage;
  }
  let pool = statePools[poolStage] || [];
  if (pool.length === 0) {
    // 兜底：先向低 stage 降级，再向高 stage 补位（ULTIMATE_CONTAINMENT 仅存 stage4 语料）
    for (let s = poolStage; s >= 1; s--) {
      if (statePools[s] && statePools[s].length > 0) {
        pool = statePools[s];
        break;
      }
    }
    if (pool.length === 0) {
      for (let s = poolStage; s <= 4; s++) {
        if (statePools[s] && statePools[s].length > 0) {
          pool = statePools[s];
          break;
        }
      }
    }
    if (pool.length === 0) {
      pool = VERDICT_POOLS.HIGH_STABILITY[currentStage] || VERDICT_POOLS.HIGH_STABILITY[1];
    }
  }

  // 使用确定性散列以避免页面重绘时乱跳，同时换题时保证连续题目不重复
  let hash = 0;
  const answerStr = Object.entries(answers)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('');
  for (let i = 0; i < answerStr.length; i++) {
    hash = (hash * 31 + answerStr.charCodeAt(i)) % 1000003;
  }
  const selectedIndex = (Math.abs(hash) + currentIndex) % pool.length;
  const selectedText = pool[selectedIndex];

  const stageNames: Record<number, string> = {
    1: 'STAGE 1: 现实基准',
    2: 'STAGE 2: 性格微异',
    3: 'STAGE 3: 荒诞偏离',
    4: 'STAGE 4: 现实失控',
  };

  return {
    text: selectedText,
    level,
    stateTag,
    badgeText: STATE_BADGE_MAP[stateTag] || stateTag,
    stageCode: stageNames[currentStage],
  };
}
